/**
 * Hook useItemCatalog — catálogo dos campos selecionáveis do item do pedido (0071).
 *
 * Uma query única carrega TODAS as opções ativas (catálogo pequeno, ~600 linhas)
 * e o CatalogCombobox filtra em memória por field_key. Espelha o padrão de
 * useLojas: tabela global (GLOBAL_TABLES em db.ts), staleTime 5min.
 *
 * Escrita: createOption é o "+ Cadastrar" do combobox. O gate de role é do
 * SERVIDOR (assertWriteAllowed em db-write-gate.ts): item_catalog_options é só do
 * administrador (qualquer field_key, inclusive codigo_modelagem). O front apenas esconde a
 * opção (canCreate) — a autoridade é o backend.
 */
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { db } from '@/lib/db';
import { api } from '@/lib/api';

// Produto (tipo_produto) saiu do catálogo em 0073 — virou produto_vinculado,
// atributo de texto do código de modelagem (um cadastro só). Os campos abaixo
// são os que têm lista própria gerenciável em /cadastros.
// `cor_gola` (0109) é a tabela do FORNECEDOR de gola/punho de polo — uma só
// para os dois — separada de `cor_malha` para não poluir a lista da vendedora.
export type CatalogFieldKey =
  | 'tecido'
  | 'cor_malha'
  | 'cor_gola'
  | 'tamanho_estampa'
  | 'localizacao_estampa'
  | 'codigo_modelagem';

export interface CatalogOption {
  id: string;
  field_key: CatalogFieldKey;
  value: string;
  active: boolean;
  /** Só em codigo_modelagem (0072): produto preenchido/travado pelo código */
  produto_vinculado: string | null;
  /** Só em codigo_modelagem (0072): código especial com produto texto livre */
  produto_livre: boolean;
}

/** Normaliza para busca: MAIÚSCULA sem acento — "ALGODAO" acha "ALGODÃO". */
export const searchNormalize = (s: string): string =>
  s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();

const QUERY_KEY = ['item-catalog'];

export function useItemCatalog() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<CatalogOption[]> => {
      const { data, error } = await db
        .from('item_catalog_options')
        .select('id, field_key, value, active, produto_vinculado, produto_livre')
        .eq('active', true)
        .order('value', { ascending: true });
      if (error) throw error;
      return (data ?? []) as CatalogOption[];
    },
    staleTime: 5 * 60 * 1000,
  });

  const byField = useMemo(() => {
    const m = new Map<CatalogFieldKey, string[]>();
    for (const opt of query.data ?? []) {
      const list = m.get(opt.field_key) ?? [];
      list.push(opt.value);
      m.set(opt.field_key, list);
    }
    return m;
  }, [query.data]);

  // Vínculo modelagem→produto (0072): codigo → { produto_vinculado, produto_livre }
  const modelagemInfo = useMemo(() => {
    const m = new Map<string, { produtoVinculado: string | null; produtoLivre: boolean }>();
    for (const opt of query.data ?? []) {
      if (opt.field_key !== 'codigo_modelagem') continue;
      m.set(opt.value, { produtoVinculado: opt.produto_vinculado, produtoLivre: opt.produto_livre });
    }
    return m;
  }, [query.data]);

  const createOption = useMutation({
    mutationFn: async ({ fieldKey, value }: { fieldKey: CatalogFieldKey; value: string }) => {
      const normalized = value.trim().toUpperCase();
      if (!normalized) throw new Error('Valor vazio');
      const { error } = await db
        .from('item_catalog_options')
        .insert({ field_key: fieldKey, value: normalized });
      if (error) throw error;
      return normalized;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  return {
    byField,
    modelagemInfo,
    isLoading: query.isLoading,
    createOption,
  };
}

// ── Administração (página /cadastros) ───────────────────────────────────
// Linhas completas (inclusive inativas) + rename/toggle/excluir/mesclar e
// vínculo modelagem→produto. Mutations invalidam também o cache do combobox
// (QUERY_KEY) para refletir na hora no modal.

const ADMIN_QUERY_KEY = ['item-catalog-admin'];

export interface CatalogOptionRow extends CatalogOption {
  created_by: string | null;
  created_at: string | null;
}

export function useItemCatalogAdmin() {
  const queryClient = useQueryClient();
  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEY });
  };

  const query = useQuery({
    queryKey: ADMIN_QUERY_KEY,
    queryFn: async (): Promise<CatalogOptionRow[]> => {
      const { data, error } = await db
        .from('item_catalog_options')
        .select('id, field_key, value, active, produto_vinculado, produto_livre, created_by, created_at')
        .order('value', { ascending: true });
      if (error) throw error;
      return (data ?? []) as CatalogOptionRow[];
    },
    staleTime: 60 * 1000,
  });

  // Pelo servidor, não pelo adapter: renomear tecido/cor precisa acompanhar
  // os MATERIAIS do estoque (que casam com o item por igualdade). Direto no
  // /api/db o catálogo mudava e o material ficava com o nome velho.
  const renameOption = useMutation({
    mutationFn: async ({ id, value }: { id: string; value: string }) => {
      const normalized = value.trim().toUpperCase();
      if (!normalized) throw new Error('Valor vazio');
      const { error } = await api.invoke('catalog/rename', { body: { id, value: normalized } });
      if (error) throw error;
    },
    onSuccess: invalidateAll,
  });

  const toggleOption = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      const { error } = await db
        .from('item_catalog_options')
        .update({ active, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: invalidateAll,
  });

  const addOption = useMutation({
    mutationFn: async ({ fieldKey, value, produtoVinculado, produtoLivre }: {
      fieldKey: CatalogFieldKey;
      value: string;
      /** Só faz sentido para codigo_modelagem — vincula o produto já na mesma inserção. */
      produtoVinculado?: string | null;
      produtoLivre?: boolean;
    }) => {
      const normalized = value.trim().toUpperCase();
      if (!normalized) throw new Error('Valor vazio');
      const row: Record<string, unknown> = { field_key: fieldKey, value: normalized };
      if (fieldKey === 'codigo_modelagem' && (produtoVinculado || produtoLivre)) {
        row.produto_vinculado = produtoLivre ? null : (produtoVinculado?.trim().toUpperCase() || null);
        row.produto_livre = !!produtoLivre;
      }
      const { error } = await db
        .from('item_catalog_options')
        .insert(row);
      if (error) throw error;
    },
    onSuccess: invalidateAll,
  });

  const deleteOption = useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const { error } = await db.from('item_catalog_options').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: invalidateAll,
  });

  // Vínculo modelagem→produto (0072): rota dedicada que grava E PROPAGA a
  // descrição para os pedidos que já usam o código (backup + reprojeção no
  // servidor). Sem isso, a descrição nova só apareceria ao reabrir cada card.
  const updateModelagemLink = useMutation({
    mutationFn: async ({ id, produtoVinculado, produtoLivre }: {
      id: string; produtoVinculado: string | null; produtoLivre: boolean;
    }) => {
      const { data, error } = await api.invoke('catalog/modelagem-link', {
        body: { id, produto_vinculado: produtoVinculado, produto_livre: produtoLivre },
      });
      if (error) throw new Error(error.message);
      return data as { codigo: string; jobs: number; backupTable: string | null; projected: number };
    },
    onSuccess: invalidateAll,
  });

  // Mesclagem: reescreve pedidos antigos (backup + reprojeção) no servidor e
  // remove as opções absorvidas — rota /api/catalog/merge (lib única de
  // normalização). `from` aceita um valor ou vários (mesclar em lote).
  const mergeOption = useMutation({
    mutationFn: async ({ fieldKey, from, to }: { fieldKey: CatalogFieldKey; from: string | string[]; to: string }) => {
      const { data, error } = await api.invoke('catalog/merge', {
        body: { field_key: fieldKey, from, to },
      });
      if (error) throw new Error(error.message);
      return data as { jobs: number; backupTable: string | null; projected: number; froms: string[] };
    },
    onSuccess: invalidateAll,
  });

  return {
    options: query.data ?? [],
    isLoading: query.isLoading,
    renameOption,
    toggleOption,
    addOption,
    deleteOption,
    updateModelagemLink,
    mergeOption,
  };
}

/** Contagem de uso de um valor nos pedidos (aviso de Excluir/Mesclar). */
export async function fetchCatalogUsage(fieldKey: CatalogFieldKey, value: string): Promise<{ jobs: number; ocorrencias: number }> {
  const { data, error } = await api.invoke('catalog/usage', { body: { field_key: fieldKey, value } });
  if (error) throw new Error(error.message);
  return data as { jobs: number; ocorrencias: number };
}

// ── Interruptor dos campos selecionáveis (rollout controlado) ───────────
// O modal Editar Item só troca texto livre → comboboxes quando o gestor
// terminar a curadoria dos cadastros e LIGAR o modo no painel /cadastros.
// Mesmo mecanismo do intelligence_mode: environment_config (org-scoped;
// escrita já negada a vendedor/produção pelo assertWriteAllowed do db.ts).

export type ItemCatalogMode = 'OFF' | 'ON';
const MODE_KEY = 'item_catalog_selects';
const MODE_QUERY_KEY = ['item-catalog-mode'];

export function useItemCatalogMode() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: MODE_QUERY_KEY,
    queryFn: async (): Promise<ItemCatalogMode> => {
      // maybeSingle: linha ausente = OFF (default seguro — modal continua texto livre)
      const { data, error } = await db
        .from('environment_config')
        .select('config_value')
        .eq('config_key', MODE_KEY)
        .maybeSingle();
      if (error) throw error;
      return data?.config_value === 'ON' ? 'ON' : 'OFF';
    },
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });

  const setMode = useMutation({
    mutationFn: async (newMode: ItemCatalogMode) => {
      // update-ou-insert: a linha só nasce no primeiro toggle
      const { data, error } = await db
        .from('environment_config')
        .update({ config_value: newMode, updated_at: new Date().toISOString() })
        .eq('config_key', MODE_KEY)
        .select('config_key');
      if (error) throw error;
      if (!data || (data as unknown[]).length === 0) {
        const { error: insErr } = await db
          .from('environment_config')
          .insert({ config_key: MODE_KEY, config_value: newMode });
        if (insErr) throw insErr;
      }
      return newMode;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MODE_QUERY_KEY });
    },
  });

  return {
    mode: query.data,
    isLoading: query.isLoading,
    setMode,
  };
}
